<script setup>
import { computed, onBeforeUnmount, ref, watch, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import {
  companyListReturnQuery,
  companyDetailTab,
} from 'dashboard/components-next/Companies/companyNavigation';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';
import CompanyCustomAttributes from 'dashboard/components-next/Companies/CompanyDetail/CompanyCustomAttributes.vue';
import { useAlert } from 'dashboard/composables';

import Policy from 'dashboard/components/policy.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CompaniesDetailsLayout from 'dashboard/components-next/Companies/CompaniesDetailsLayout.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import CompanyContactsSidebar from 'dashboard/components-next/Companies/CompanyDetail/CompanyContactsSidebar.vue';
import CompanyHistorySidebar from 'dashboard/components-next/Companies/CompanyDetail/CompanyHistorySidebar.vue';
import CompanyNotesSidebar from 'dashboard/components-next/Companies/CompanyDetail/CompanyNotesSidebar.vue';
import CompanyProfileCard from 'dashboard/components-next/Companies/CompanyDetail/CompanyProfileCard.vue';
import ConfirmCompanyDeleteDialog from 'dashboard/components-next/Companies/CompanyDetail/ConfirmCompanyDeleteDialog.vue';
import { useCompaniesStore } from 'dashboard/stores/companies';

const route = useRoute();
const router = useRouter();
const companiesStore = useCompaniesStore();
const { t } = useI18n();
const { isWorkspaceV2 } = useWorkspaceV2();
const errors = reactive({
  profile: '',
  contacts: '',
  notes: '',
  history: '',
  search: '',
  relation: '',
  deletion: '',
});
const relationPending = ref(false);
const deletePending = ref(false);

const confirmDeleteDialogRef = ref(null);
const selectedCandidate = ref(null);
const activeSidebarTab = ref(
  isWorkspaceV2.value ? companyDetailTab(route.query.tab) : 'history'
);

const companyId = computed(() => Number(route.params.companyId));
const company = computed(() => companiesStore.getRecord(companyId.value));
const companyContacts = computed(() => companiesStore.companyContacts);
const companyContactsMeta = computed(() => companiesStore.companyContactsMeta);
const companyConversations = computed(
  () => companiesStore.companyConversations || []
);
const companyNotes = computed(() => companiesStore.companyNotes || []);
const contactSearchResults = computed(
  () => companiesStore.contactSearchResults
);
const uiFlags = computed(() => companiesStore.getUIFlags);

const isFetchingCompany = computed(() => uiFlags.value.fetchingItem);
const isFetchingContacts = computed(() => uiFlags.value.fetchingContacts);
const isFetchingConversations = computed(
  () => uiFlags.value.fetchingConversations
);
const isFetchingNotes = computed(() => uiFlags.value.fetchingNotes);
const isSearchingContacts = computed(() => uiFlags.value.searchingContacts);
const isManagingContacts = computed(
  () => uiFlags.value.creatingContact || uiFlags.value.removingContact
);
const isDeletingCompany = computed(() => uiFlags.value.deletingItem);
const hasCompany = computed(() => Boolean(company.value?.id));
const showInitialLoadingState = computed(
  () =>
    !hasCompany.value && (isFetchingCompany.value || isFetchingContacts.value)
);

const breadcrumbItems = computed(() => [
  { label: t('COMPANIES.HEADER') },
  ...(hasCompany.value
    ? [{ label: company.value?.name || t('COMPANIES.UNNAMED') }]
    : []),
]);

const SIDEBAR_TABS_OPTIONS = [
  { key: 'HISTORY', value: 'history' },
  { key: 'NOTES', value: 'notes' },
  { key: 'CONTACTS', value: 'contacts' },
];

const sidebarTabs = computed(() =>
  [
    ...SIDEBAR_TABS_OPTIONS,
    ...(isWorkspaceV2.value
      ? [{ key: 'ATTRIBUTES', value: 'attributes' }]
      : []),
  ].map(tab => ({
    label: {
      notes: t('COMPANIES.DETAIL.SIDEBAR.TABS.NOTES'),
      history: t('COMPANIES.DETAIL.SIDEBAR.TABS.HISTORY'),
      attributes: t('WORKSPACE_V2.COMPANY_DETAIL.ATTRIBUTES'),
      contacts: `${t('COMPANIES.DETAIL.SIDEBAR.TABS.CONTACTS')} (${Number(companyContactsMeta.value.totalCount || 0)})`,
    }[tab.value],
    value: tab.value,
  }))
);

const activeSidebarTabIndex = computed(() =>
  SIDEBAR_TABS_OPTIONS.findIndex(tab => tab.value === activeSidebarTab.value)
);

const goToCompaniesIndex = () => {
  router.push({
    name: 'companies_dashboard_index',
    params: { accountId: route.params.accountId },
    query: isWorkspaceV2.value
      ? companyListReturnQuery(route.query)
      : { page: '1' },
  });
};

const goToCompaniesList = () => {
  if (!isWorkspaceV2.value && window.history.state?.back) {
    router.back();
    return;
  }
  goToCompaniesIndex();
};

const readTokens = {};
const readSection = async (section, action) => {
  const token = (readTokens[section] || 0) + 1;
  readTokens[section] = token;
  const id = companyId.value;
  const accountId = route.params.accountId;
  errors[section] = '';
  try {
    await action();
  } catch {
    if (
      id === companyId.value &&
      accountId === route.params.accountId &&
      readTokens[section] === token
    )
      errors[section] = t('WORKSPACE_V2.COMPANY_DETAIL.LOAD_ERROR');
  }
};
const contactsPage = computed(() => {
  const page = Number(route.query.contactsPage);
  return Number.isInteger(page) && page > 0 ? page : 1;
});
const loadContacts = () =>
  readSection('contacts', () =>
    companiesStore.getCompanyContacts(companyId.value, contactsPage.value)
  );
watch(contactsPage, loadContacts);
const loadCompanyContactsPage = async page => {
  if (!companyId.value) return;
  if (isWorkspaceV2.value && page !== contactsPage.value) {
    await router.replace({
      query: { ...route.query, contactsPage: String(page) },
    });
    return;
  }
  await readSection('contacts', () =>
    companiesStore.getCompanyContacts(companyId.value, page)
  );
};
const retryProfile = () =>
  readSection('profile', () => companiesStore.show(companyId.value));

const openDeleteCompanyDialog = () => {
  confirmDeleteDialogRef.value?.dialogRef.open();
};

const clearSelectedCandidate = () => {
  selectedCandidate.value = null;
};

const historyPage = computed(() =>
  Math.max(1, Math.trunc(Number(route.query.historyPage) || 1))
);
const loadHistory = () =>
  readSection('history', () =>
    companiesStore.getCompanyConversations(companyId.value, historyPage.value)
  );
const changeHistoryPage = page =>
  router.replace({ query: { ...route.query, historyPage: String(page) } });
watch(historyPage, loadHistory);

const notesPage = computed(() =>
  Math.max(1, Math.trunc(Number(route.query.notesPage) || 1))
);
const loadNotes = () =>
  readSection('notes', () =>
    companiesStore.getCompanyNotes(companyId.value, notesPage.value)
  );
const changeNotesPage = page =>
  router.replace({ query: { ...route.query, notesPage: String(page) } });
watch(notesPage, loadNotes);

const loadSidebarTab = tab => {
  if (!companyId.value) return;
  if (tab === 'notes') loadNotes();
  if (tab === 'history') loadHistory();
};

const handleSidebarTabChange = tab => {
  if (isWorkspaceV2.value) {
    router.replace({ query: { ...route.query, tab: tab.value } });
  } else {
    activeSidebarTab.value = tab.value;
    loadSidebarTab(tab.value);
  }
};
watch(
  () => route.query.tab,
  value => {
    if (!isWorkspaceV2.value) return;
    activeSidebarTab.value = companyDetailTab(value);
    loadSidebarTab(activeSidebarTab.value);
  }
);

const moveTab = (event, index) => {
  if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
  event.preventDefault();
  const count = sidebarTabs.value.length;
  let target = (index + (event.key === 'ArrowRight' ? 1 : count - 1)) % count;
  if (event.key === 'Home') target = 0;
  if (event.key === 'End') target = count - 1;
  event.currentTarget.parentElement
    .querySelectorAll('[role="tab"]')
    [target]?.focus();
  handleSidebarTabChange(sidebarTabs.value[target]);
};
const handleContactSearch = async (query, page = 1) => {
  await readSection('search', () =>
    companiesStore.searchCompanyContactCandidates({
      companyId: companyId.value,
      search: query,
      page,
    })
  );
};

const handleConfirmContactSelection = async () => {
  const candidate = selectedCandidate.value;
  if (!candidate || relationPending.value) return;
  const id = companyId.value;
  const accountId = route.params.accountId;
  const isCurrentContext = () =>
    id === companyId.value && accountId === route.params.accountId;
  relationPending.value = true;
  errors.relation = '';

  const isReassigning =
    candidate.company?.id && candidate.company.id !== companyId.value;
  const message = isReassigning
    ? t('COMPANIES.DETAIL.CONTACTS.MESSAGES.REASSIGN_SUCCESS')
    : t('COMPANIES.DETAIL.CONTACTS.MESSAGES.ADD_SUCCESS');

  try {
    await companiesStore.attachContactToCompany(companyId.value, candidate.id);
    if (!isCurrentContext()) return;
    useAlert(message);
    clearSelectedCandidate();
  } catch {
    if (!isCurrentContext()) return;
    const errorMessage = isReassigning
      ? t('COMPANIES.DETAIL.CONTACTS.MESSAGES.REASSIGN_ERROR')
      : t('COMPANIES.DETAIL.CONTACTS.MESSAGES.ADD_ERROR');
    if (isCurrentContext()) errors.relation = errorMessage;
    useAlert(errorMessage);
  } finally {
    if (isCurrentContext()) relationPending.value = false;
  }
};

const handleRemoveContact = async contactId => {
  if (relationPending.value) return;
  const id = companyId.value;
  const accountId = route.params.accountId;
  const isCurrentContext = () =>
    id === companyId.value && accountId === route.params.accountId;
  relationPending.value = true;
  errors.relation = '';
  const currentPage = Number(companyContactsMeta.value.page || 1);
  const nextPage =
    currentPage > 1 && companyContacts.value.length === 1
      ? currentPage - 1
      : currentPage;

  try {
    await companiesStore.removeContactFromCompany(
      companyId.value,
      contactId,
      nextPage
    );
    if (!isCurrentContext()) return;
    if (isWorkspaceV2.value) {
      router.replace({
        query: { ...route.query, contactsPage: String(nextPage) },
      });
    }
    useAlert(t('COMPANIES.DETAIL.CONTACTS.MESSAGES.REMOVE_SUCCESS'));
  } catch {
    if (!isCurrentContext()) return;
    if (isCurrentContext())
      errors.relation = t('COMPANIES.DETAIL.CONTACTS.MESSAGES.REMOVE_ERROR');
    useAlert(t('COMPANIES.DETAIL.CONTACTS.MESSAGES.REMOVE_ERROR'));
  } finally {
    if (isCurrentContext()) relationPending.value = false;
  }
};

const handleDeleteCompany = async () => {
  if (deletePending.value || isDeletingCompany.value) return;
  const id = companyId.value;
  const accountId = route.params.accountId;
  const isCurrentContext = () =>
    id === companyId.value && accountId === route.params.accountId;
  deletePending.value = true;
  errors.deletion = '';
  try {
    await companiesStore.delete(id);
    if (!isCurrentContext()) return;
    useAlert(t('COMPANIES.DETAIL.DELETE.MESSAGES.SUCCESS'));
    confirmDeleteDialogRef.value?.dialogRef.close();
    goToCompaniesIndex();
  } catch {
    if (!isCurrentContext()) return;
    if (isCurrentContext())
      errors.deletion = t('COMPANIES.DETAIL.DELETE.MESSAGES.ERROR');
    useAlert(t('COMPANIES.DETAIL.DELETE.MESSAGES.ERROR'));
  } finally {
    if (isCurrentContext()) deletePending.value = false;
  }
};

watch(
  [() => route.params.accountId, companyId],
  async ([, id]) => {
    companiesStore.resetCompanyDetailState();
    clearSelectedCandidate();
    relationPending.value = false;
    deletePending.value = false;
    activeSidebarTab.value = isWorkspaceV2.value
      ? companyDetailTab(route.query.tab)
      : 'history';
    Object.keys(errors).forEach(key => {
      errors[key] = '';
    });
    if (!id) return;
    await Promise.allSettled([
      readSection('profile', () => companiesStore.show(id)),
      readSection('contacts', () =>
        companiesStore.getCompanyContacts(id, contactsPage.value)
      ),
      loadHistory(),
      ...(activeSidebarTab.value === 'notes' ? [loadNotes()] : []),
    ]);
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  companiesStore.resetCompanyDetailState();
});
</script>

<template>
  <CompaniesDetailsLayout
    data-testid="page-company-detail"
    :breadcrumb-items="breadcrumbItems"
    @back="goToCompaniesList"
  >
    <div
      v-if="isWorkspaceV2 && errors.profile"
      role="alert"
      class="mb-4 rounded-xl border border-n-weak p-4 text-sm text-n-ruby-11"
    >
      <p>{{ errors.profile }}</p>
      <Button
        :label="t('WORKSPACE_V2.COMPANY_DETAIL.RETRY')"
        @click="retryProfile"
      />
    </div>
    <div
      v-if="showInitialLoadingState"
      class="flex flex-col items-center justify-center gap-3 py-24 text-n-slate-11"
    >
      <Spinner />
      <span class="text-sm">{{ t('COMPANIES.DETAIL.LOADING') }}</span>
    </div>

    <div
      v-else-if="!hasCompany && !errors.profile"
      class="flex flex-col items-center justify-center gap-3 px-6 py-24 text-center rounded-2xl border border-n-weak bg-n-solid-2"
    >
      <span class="text-lg font-medium text-n-slate-12">
        {{ t('COMPANIES.DETAIL.EMPTY_STATE.TITLE') }}
      </span>
      <p class="max-w-md text-sm text-n-slate-11">
        {{ t('COMPANIES.DETAIL.EMPTY_STATE.SUBTITLE') }}
      </p>
    </div>

    <div v-else-if="hasCompany" class="flex flex-col gap-6">
      <CompanyProfileCard :company="company" :is-loading="isFetchingCompany" />

      <Policy :permissions="['administrator']">
        <section
          class="flex flex-col items-start w-full gap-4 pt-6 border-t border-n-strong"
        >
          <div class="flex flex-col gap-2">
            <h6 class="text-base font-medium text-n-slate-12">
              {{ t('COMPANIES.DETAIL.DELETE.SECTION_TITLE') }}
            </h6>
            <span class="text-sm text-n-slate-11">
              {{ t('COMPANIES.DETAIL.DELETE.SECTION_DESCRIPTION') }}
            </span>
          </div>
          <Button
            :label="t('COMPANIES.DETAIL.DELETE.BUTTON')"
            color="ruby"
            :disabled="isDeletingCompany"
            @click="openDeleteCompanyDialog"
          />
        </section>
      </Policy>
    </div>

    <template #sidebarHeader>
      <div class="px-6 pt-6 pb-3">
        <div
          v-if="isWorkspaceV2"
          class="flex flex-wrap gap-1"
          role="tablist"
          :aria-label="t('WORKSPACE_V2.COMPANY_DETAIL.SECTIONS')"
        >
          <button
            v-for="(tab, index) in sidebarTabs"
            :id="`company-tab-${tab.value}`"
            :key="tab.value"
            type="button"
            role="tab"
            :aria-selected="activeSidebarTab === tab.value"
            :tabindex="activeSidebarTab === tab.value ? 0 : -1"
            aria-controls="company-section"
            class="rounded-full px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-n-brand"
            :class="
              activeSidebarTab === tab.value
                ? 'bg-n-slate-12 text-n-solid-1'
                : 'text-n-slate-11 hover:bg-n-alpha-2'
            "
            @keydown="moveTab($event, index)"
            @click="handleSidebarTabChange(tab)"
          >
            {{ tab.label }}
          </button>
        </div>
        <TabBar
          v-else
          :tabs="sidebarTabs"
          :initial-active-tab="activeSidebarTabIndex"
          class="w-full [&>button]:w-full bg-n-alpha-black2"
          @tab-changed="handleSidebarTabChange"
        />
      </div>
    </template>
    <template v-if="hasCompany" #sidebar>
      <section
        id="company-section"
        :role="isWorkspaceV2 ? 'tabpanel' : undefined"
        :aria-labelledby="
          isWorkspaceV2 ? `company-tab-${activeSidebarTab}` : undefined
        "
      >
        <div
          v-if="isWorkspaceV2 && errors[activeSidebarTab]"
          role="alert"
          class="mx-6 mb-4 rounded-xl border border-n-weak p-3 text-sm text-n-ruby-11"
        >
          <p>{{ errors[activeSidebarTab] }}</p>
          <Button
            :label="t('WORKSPACE_V2.COMPANY_DETAIL.RETRY')"
            @click="
              activeSidebarTab === 'contacts'
                ? loadCompanyContactsPage(Number(companyContactsMeta.page || 1))
                : loadSidebarTab(activeSidebarTab)
            "
          />
        </div>
        <CompanyCustomAttributes
          v-if="isWorkspaceV2 && activeSidebarTab === 'attributes'"
          :key="`${route.params.accountId}-${companyId}`"
          :company="company"
        />
        <CompanyNotesSidebar
          v-if="activeSidebarTab === 'notes' && !errors.notes"
          :notes="companyNotes"
          :meta="companiesStore.companyNotesMeta"
          :is-loading="isFetchingNotes"
          @update:current-page="changeNotesPage"
        />
        <CompanyHistorySidebar
          v-if="activeSidebarTab === 'history' && !errors.history"
          :conversations="companyConversations"
          :meta="companiesStore.companyConversationsMeta"
          :is-loading="isFetchingConversations"
          @update:current-page="changeHistoryPage"
        />
        <CompanyContactsSidebar
          v-if="activeSidebarTab === 'contacts' && !errors.contacts"
          :company="company"
          :contacts="companyContacts"
          :meta="companyContactsMeta"
          :is-loading="isFetchingContacts"
          :is-busy="isManagingContacts || relationPending"
          :error="errors.relation"
          :search-error="errors.search"
          :search-results="contactSearchResults"
          :search-meta="companiesStore.contactSearchMeta"
          :is-searching="isSearchingContacts"
          :selected-contact="selectedCandidate"
          @cancel-contact-selection="clearSelectedCandidate"
          @confirm-contact-selection="handleConfirmContactSelection"
          @search="handleContactSearch"
          @select-contact="contact => (selectedCandidate = contact)"
          @remove-contact="handleRemoveContact"
          @update:current-page="loadCompanyContactsPage"
        />
      </section>
    </template>

    <ConfirmCompanyDeleteDialog
      ref="confirmDeleteDialogRef"
      :company="company"
      :is-loading="isDeletingCompany || deletePending"
      :error="errors.deletion"
      @confirm="handleDeleteCompany"
    />
  </CompaniesDetailsLayout>
</template>
